// ?use_ios_ui (2026-10-17) — the iOS 0.7.0 placement flow, composed on top
// of the shipped WheelPlacementController rather than changing it, so the
// flag-off paths are the shipped code with nothing in between.
//
// Twin of the iOS-only parts of `WheelPlacementController.swift` +
// `ManualPlacementViewModel.swift` (branch wheel-sizing):
//   - a tap only selects or deselects; the Place button places at the view
//     centre and lands in View, nothing selected;
//   - the Place screen (`isPlaceUIOpen`), opened by `+`, closed by Place /
//     Cancel, reopened by reset — not by a delete;
//   - each placed wheel snapshots its own size options; the menu and the
//     pinch resize across the face only (WheelSizeOptions / WheelSizePinch);
//   - the "current wheel size" answer derives each wheel's MASK band.

import type { WheelPlacementController } from '@/controllers/wheel-placement-controller'
import { DEFAULT_MAX_WHEELS } from '@/controllers/wheel-placement-controller'
import type { WheelLoaderLike } from '@/controllers/wheel-placement-controller'
import type { ScreenPoint, WheelId, WheelRenderer } from '@/ar/wheel-renderer'
import type { WheelScale } from '@/models/wheel-scale'
import { currentWheelSize, type TCurrentWheelSize } from '@/models/current-wheel-size'
import { bandFadeWidth, borderWidth } from '@/models/border-width'
import {
  sizeOptionsReference,
  sizeScale,
  wheelSizeOptions,
  type TWheelSizeOptions,
} from '@/models/wheel-size-options'
import { WheelSizePinch } from '@/models/wheel-size-pinch'
import { atoLog } from '@/services/ato-log'

/** iOS WheelScaleGlide.duration — long enough to see the wheel travel. */
export const SIZE_GLIDE_MS = 200

export interface IosPlacementState {
  readonly isPlaceUIOpen: boolean
  readonly isAskingCurrentWheelSize: boolean
  readonly currentWheelSize: TCurrentWheelSize | null
  readonly canAddWheel: boolean
  readonly sizeOptionsForSelected: TWheelSizeOptions
  readonly shownSizeForSelected: number | null
  /** The selected wheel's diameter the MASK row seeds from: its shown
   *  size, else its estimate; null hides the row. */
  readonly ringSizeInchesForSelected: number | null
}

interface HandleTapInput {
  screenPoint: ScreenPoint
  viewSize: { width: number; height: number }
  isLandscape: boolean
}

interface WheelSizing {
  readonly options: TWheelSizeOptions
  /** The scale the wheel was placed at — every resize multiplies from it. */
  readonly placedScale: WheelScale
  /** The listed size committed now; null for fixed / estimated / hidden. */
  shown: number | null
}

export interface IosPlacementControllerConfig {
  controller: WheelPlacementController
  renderer: WheelRenderer
  loader: WheelLoaderLike
  onToast?: (message: string) => void
  /** The pinch committed another listed size — main.ts buzzes. */
  onSizeStep?: () => void
  maxWheels?: number
  /** Injectable clock + frame scheduler for the glide. Defaults to
   *  performance.now + a 16 ms timer: window.requestAnimationFrame is
   *  not dependable while an immersive-ar session owns the display. */
  now?: () => number
  scheduleFrame?: (callback: () => void) => void
}

type Listener = () => void

export class IosPlacementController {
  private readonly controller: WheelPlacementController
  private readonly renderer: WheelRenderer
  private readonly loader: WheelLoaderLike
  private readonly onToast: ((message: string) => void) | undefined
  private readonly onSizeStep: (() => void) | undefined
  private readonly maxWheels: number
  private readonly now: () => number
  private readonly scheduleFrame: (callback: () => void) => void

  private isPlaceUIOpen = true
  private isAskingCurrentWheelSize = true
  private answer: TCurrentWheelSize | null = null
  private readonly sizing = new Map<WheelId, WheelSizing>()
  private pinch: { id: WheelId; model: WheelSizePinch } | null = null
  /** Bumped by every glide start, so a superseded glide stops writing. */
  private glideGeneration = 0
  private state: IosPlacementState
  private readonly listeners = new Set<Listener>()

  constructor(config: IosPlacementControllerConfig) {
    this.controller = config.controller
    this.renderer = config.renderer
    this.loader = config.loader
    this.onToast = config.onToast
    this.onSizeStep = config.onSizeStep
    this.maxWheels = config.maxWheels ?? DEFAULT_MAX_WHEELS
    this.now = config.now ?? (() => performance.now())
    this.scheduleFrame = config.scheduleFrame ?? ((callback) => void setTimeout(callback, 16))
    this.state = this.derive()
    this.controller.subscribe(() => this.onBaseChange())
  }

  getState(): IosPlacementState {
    return this.state
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  // ── Screens ──────────────────────────────────────────────────────────

  /** The Place button: the wheel goes where the ring is. */
  placeFramed(input: HandleTapInput): boolean {
    const id = this.controller.placeAtViewCenter(input)
    if (id == null) return false
    this.recordSizing(id)
    this.applyBand(id)
    this.isPlaceUIOpen = false
    this.controller.deselectAll()
    this.emit()
    return true
  }

  /** `+`, from View or Edit. Deselects so the panel of the wheel being
   *  edited never sits under the ring for the next one. */
  openPlaceUI(): void {
    if (this.controller.getState().placedWheelCount >= this.maxWheels) {
      this.onToast?.(`Maximum of ${this.maxWheels} wheels — tap one to adjust it`)
      return
    }
    this.controller.deselectAll()
    this.isPlaceUIOpen = true
    this.emit()
  }

  /** Cancel, under Place. Shown even with nothing placed; `+` is the way back. */
  cancelPlaceUI(): void {
    this.isPlaceUIOpen = false
    this.emit()
  }

  // ── Tap ──────────────────────────────────────────────────────────────

  /** A tap only ever selects or deselects. */
  handleTap(input: HandleTapInput): void {
    const hit = this.renderer.hitTestWheel(input.screenPoint)
    if (hit != null) {
      this.controller.selectWheel(hit)
      return
    }
    if (this.controller.getState().selectedWheelId != null) this.controller.deselectAll()
  }

  // ── Delete / reset ───────────────────────────────────────────────────

  /** Leaves View with nothing selected — the shipped delete auto-selects
   *  the remaining wheel, which the iOS flow does not. Does NOT reopen Place. */
  deleteSelectedWheel(): void {
    const id = this.controller.getState().selectedWheelId
    if (id == null) return
    this.controller.deleteSelectedWheel()
    this.sizing.delete(id)
    this.controller.deselectAll()
    this.emit()
  }

  /** An empty scene is the first screen again, and the first screen is Place. */
  resetAll(): void {
    this.controller.resetAll()
    this.sizing.clear()
    this.pinch = null
    this.isPlaceUIOpen = true
    this.emit()
  }

  // ── Current wheel size (MASK) ────────────────────────────────────────

  /** The answer describes the car, not a wheel: no selection needed. */
  setCurrentWheelSize(inches: number): void {
    const next = currentWheelSize(inches)
    if (this.answer?.inches === next.inches) return
    this.answer = next
    atoLog.event('current_wheel_size_set', { inches: next.inches })
    for (const id of this.sizing.keys()) this.applyBand(id)
    this.emit()
  }

  /** On Select or Skip. A skip writes no answer — every wheel parks at its
   *  own size, the one-inch buffer. */
  dismissCurrentWheelSizeQuestion(): void {
    this.isAskingCurrentWheelSize = false
    this.emit()
  }

  // ── Wheel size (menu + pinch) ────────────────────────────────────────

  /** A size picked from the menu: committed at once, glided to. */
  setSelectedWheelSize(inches: number): void {
    const id = this.controller.getState().selectedWheelId
    const sizing = id == null ? undefined : this.sizing.get(id)
    if (id == null || sizing == null || sizing.options.kind !== 'choice') return
    if (!sizing.options.sizes.includes(inches)) return
    sizing.shown = inches
    atoLog.event('wheel_size_selected', { wheel_id: id, inches, source: 'menu' })
    this.glide(id, this.faceScaleAt(sizing, inches))
    this.emit()
  }

  /** Fingers down. The selected wheel, else the wheel under the midpoint
   *  (which becomes selected), else nothing. */
  pinchBegin(midpoint: ScreenPoint): void {
    let id = this.controller.getState().selectedWheelId
    if (id == null) {
      id = this.renderer.hitTestWheel(midpoint)
      if (id == null) return
      this.controller.selectWheel(id)
    }
    const sizing = this.sizing.get(id)
    if (sizing == null || sizing.options.kind !== 'choice' || sizing.shown == null) return
    this.glideGeneration += 1
    this.pinch = { id, model: new WheelSizePinch({ sizes: sizing.options.sizes, shown: sizing.shown }) }
  }

  /** @param pinchScale cumulative since the fingers landed. */
  pinchChange(pinchScale: number): void {
    const pinch = this.pinch
    if (pinch == null) return
    // The wheel lost selection mid-pinch: settle it where it was drawn.
    if (this.controller.getState().selectedWheelId !== pinch.id) {
      this.pinchEnd()
      return
    }
    const sizing = this.sizing.get(pinch.id)
    if (sizing == null) return
    const stepped = pinch.model.update(pinchScale)
    this.renderer.setWheelScale(pinch.id, this.faceScaleAt(sizing, pinch.model.displayInches))
    if (stepped) {
      sizing.shown = pinch.model.shown
      this.onSizeStep?.()
      this.emit()
    }
  }

  /** Fingers up (or cancelled): settle on the nearest listed size. */
  pinchEnd(): void {
    const pinch = this.pinch
    if (pinch == null) return
    this.pinch = null
    const sizing = this.sizing.get(pinch.id)
    if (sizing == null) return
    sizing.shown = pinch.model.nearestSize
    atoLog.event('wheel_size_selected', { wheel_id: pinch.id, inches: sizing.shown, source: 'pinch' })
    this.glide(pinch.id, this.faceScaleAt(sizing, sizing.shown))
    this.emit()
  }

  // ── internal ─────────────────────────────────────────────────────────

  private recordSizing(id: WheelId): void {
    const { wheelInfo, glbDiameterInches } = this.loader.getState()
    const options = wheelSizeOptions({
      listed: wheelInfo.sizes.map((size) => size.diameterInches),
      current: wheelInfo.selectedSize?.diameterInches ?? null,
      measured: glbDiameterInches ?? null,
    })
    const placedScale = this.renderer.getWheels().find((w) => w.id === id)?.scale ?? { face: 1, width: 1 }
    this.sizing.set(id, {
      options,
      placedScale,
      shown: options.kind === 'choice' ? options.reference : null,
    })
  }

  /** The wheel's scale shown at `inches`: the placed scale times the
   *  edge-to-edge face ratio from the size it was placed at. */
  private faceScaleAt(sizing: WheelSizing, inches: number): WheelScale {
    const reference = sizeOptionsReference(sizing.options)
    if (reference == null) return sizing.placedScale
    return {
      face: sizing.placedScale.face * sizeScale({ from: reference, to: inches }).face,
      width: sizing.placedScale.width,
    }
  }

  /** Writes the scale a frame at a time, ease-out, then rebuilds the band
   *  on landing (it divides the live face scale back out). */
  private glide(id: WheelId, to: WheelScale): void {
    const from = this.renderer.getWheels().find((w) => w.id === id)?.scale
    if (from == null) return
    const generation = ++this.glideGeneration
    const start = this.now()
    const step = () => {
      if (generation !== this.glideGeneration) return
      const progress = (this.now() - start) / SIZE_GLIDE_MS
      if (progress >= 1) {
        this.renderer.setWheelScale(id, to)
        this.applyBand(id)
        return
      }
      const eased = 1 - (1 - progress) * (1 - progress)
      this.renderer.setWheelScale(id, {
        face: from.face + (to.face - from.face) * eased,
        width: from.width + (to.width - from.width) * eased,
      })
      this.scheduleFrame(step)
    }
    this.scheduleFrame(step)
  }

  private applyBand(id: WheelId): void {
    const width = borderWidth({ current: this.answer, selectedInches: this.ringSizeInches(id) })
    this.renderer.setWheelBand?.(id, width.isOff ? null : { solidM: width.quantised, fadeM: bandFadeWidth(width.quantised) })
  }

  private ringSizeInches(id: WheelId | null): number | null {
    const sizing = id == null ? undefined : this.sizing.get(id)
    if (sizing == null) return null
    switch (sizing.options.kind) {
      case 'choice':
        return sizing.shown
      case 'fixed':
      case 'estimated':
        return sizing.options.inches
      default:
        return null
    }
  }

  private onBaseChange(): void {
    // The base's own delete / reset paths drop wheels this class never
    // hears about (the shipped UI is not mounted, but stay honest).
    const live = new Set(this.controller.getState().placedWheelIds)
    for (const id of Array.from(this.sizing.keys())) if (!live.has(id)) this.sizing.delete(id)
    this.emit()
  }

  private derive(): IosPlacementState {
    const base = this.controller.getState()
    const selected = base.selectedWheelId
    const sizing = selected == null ? undefined : this.sizing.get(selected)
    return {
      isPlaceUIOpen: this.isPlaceUIOpen,
      isAskingCurrentWheelSize: this.isAskingCurrentWheelSize,
      currentWheelSize: this.answer,
      canAddWheel: base.placedWheelCount < this.maxWheels,
      sizeOptionsForSelected: sizing?.options ?? { kind: 'hidden' },
      shownSizeForSelected: sizing?.shown ?? null,
      ringSizeInchesForSelected: this.ringSizeInches(selected),
    }
  }

  private emit(): void {
    this.state = this.derive()
    for (const listener of Array.from(this.listeners)) listener()
  }
}
